import type { FC } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  formatUsd,
  type FinancialSummaryGroup,
  type FinancialSummaryRow,
} from "../model";
import { QueryErrorAlert } from "./QueryErrorAlert";
import { TrendChip } from "./TrendChip";

interface FinancialSummaryCardProps {
  groups: FinancialSummaryGroup[] | null;
  hasComparison: boolean;
  loading: boolean;
  error: boolean;
  /** Новый период грузится, на экране пока цифры прошлого. */
  refreshing?: boolean;
  onRetry: () => void;
}

const SummaryRow: FC<{ row: FinancialSummaryRow; hasComparison: boolean }> = ({
  row,
  hasComparison,
}) => {
  const weight = row.emphasis ? 700 : 400;
  return (
    <ListItem
      sx={{
        alignItems: "flex-start",
        gap: 2,
        px: 2,
        py: 1,
        borderRadius: 1,
        ...(row.emphasis ? { bgcolor: "action.hover" } : {}),
      }}
    >
      <ListItemText
        primary={row.label}
        secondary={row.note}
        sx={{ my: 0 }}
        slotProps={{
          primary: { variant: "body2", sx: { fontWeight: weight } },
          secondary: { variant: "caption" },
        }}
      />
      <Stack
        spacing={0.5}
        sx={{ alignItems: "flex-end", textAlign: "right", flexShrink: 0 }}
      >
        <Typography
          variant={row.emphasis ? "h6" : "subtitle1"}
          component="span"
          color={row.negative ? "error.main" : "text.primary"}
          sx={{
            fontWeight: row.emphasis ? 700 : 500,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.3,
          }}
        >
          {formatUsd(row.current)}
        </Typography>
        {row.snapshot ? (
          <Chip size="small" variant="outlined" label="на сегодня" />
        ) : hasComparison && row.previous !== null ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {`прошлый период: ${formatUsd(row.previous)}`}
            </Typography>
            <TrendChip trend={row.change} tone={row.tone} />
          </Stack>
        ) : null}
      </Stack>
    </ListItem>
  );
};

/**
 * «Финансовая сводка» лестницей (спека 2026-10-16 §3.2): три группы с
 * подзаголовками, у строки подпись слева и сумма крупно справа, под суммой
 * прошлый период и изменение; строки «на сегодня» помечены чипом вместо
 * сравнения; итоги выделены фоном и жирным, минус красным.
 */
export const FinancialSummaryCard: FC<FinancialSummaryCardProps> = ({
  groups,
  hasComparison,
  loading,
  error,
  refreshing = false,
  onRetry,
}) => {
  let body;
  // Ошибка важнее старых строк: устаревшие цифры под новым периодом хуже пустоты.
  if (error) {
    body = <QueryErrorAlert onRetry={onRetry} />;
  } else if (!groups) {
    body = loading ? <Skeleton variant="rounded" height={360} /> : null;
  } else {
    body = (
      <Stack divider={<Divider />} spacing={1.5}>
        {groups.map((group) => (
          <Stack
            key={group.key}
            component="section"
            aria-labelledby={`financial-summary-${group.key}`}
            spacing={0.5}
          >
            {/* Подзаголовок группы обычным регистром: структура лестницы,
                а не декоративная метка. */}
            <Typography
              id={`financial-summary-${group.key}`}
              variant="subtitle2"
              color="text.secondary"
              component="h3"
              sx={{ px: 2 }}
            >
              {group.title}
            </Typography>
            <List dense disablePadding>
              {group.rows.map((row) => (
                <SummaryRow
                  key={row.key}
                  row={row}
                  hasComparison={hasComparison}
                />
              ))}
            </List>
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <Card sx={{ position: "relative" }}>
      {refreshing && groups && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
        />
      )}
      <CardHeader
        title="Финансовая сводка"
        subheader="Начислено, выставлено, оплачено и потрачено за период"
      />
      <CardContent sx={{ pt: 0 }}>{body}</CardContent>
    </Card>
  );
};
